// v2 boar — the true wedge profile: a high shoulder hump sloping down to low
// hindquarters, stout dark legs, a blocky head dipped low ending in a
// cylindrical snout with a flat pink-grey disc, upturned white tusks, a
// three-cone bristle mane along the crest and a tasselled tail. Two coats:
// dark forest brown and grizzled grey. Ported 1:1 from age-of-mcp's v2/boar.ts,
// using the library's shared `critter` (quadLegs) helper.
import * as THREE from "three";
import { defineModel, critter, type Part } from "../../lib";

const COATS = [
  { body: "#3f2f20", hump: "#34281a", rear: "#38291c", head: "#483624", leg: "#221a10" },
  { body: "#4c4234", hump: "#403628", rear: "#443a2c", head: "#564a38", leg: "#2a241c" },
];

function boarParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.052, dz: 0.03, bodyY: 0.07, rTop: 0.011, rFoot: 0.009, color: c.leg,
  });
  parts.push(
    // barrel body, the tall shoulder hump and the lower rear
    { geometry: new THREE.IcosahedronGeometry(0.055, 0), color: c.body,
      position: [-0.005, 0.07, 0], scale: [1.6, 1.05, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.hump,
      position: [0.032, 0.098, 0], scale: [1.1, 0.95, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.rear,
      position: [-0.062, 0.072, 0], scale: [1.0, 0.88, 0.88] },
    // blocky head dipped toward the ground
    { geometry: new THREE.BoxGeometry(0.055, 0.042, 0.04), color: c.head,
      position: [0.098, 0.08, 0], rotation: [0, 0, -0.12] },
    // cylindrical snout with a flat disc nose
    { geometry: new THREE.CylinderGeometry(0.012, 0.013, 0.018, 5), color: c.head,
      position: [0.133, 0.071, 0], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.0135, 0.0135, 0.006, 6), color: "#c9a290",
      position: [0.1445, 0.071, 0], rotation: [0, 0, Math.PI / 2] },
    // upturned white tusks
    { geometry: new THREE.ConeGeometry(0.0038, 0.02, 3), color: "#ece2cc",
      position: [0.125, 0.06, 0.016], rotation: [-0.2, 0, -0.5] },
    { geometry: new THREE.ConeGeometry(0.0038, 0.02, 3), color: "#ece2cc",
      position: [0.125, 0.06, -0.016], rotation: [0.2, 0, -0.5] },
    // ears
    { geometry: new THREE.ConeGeometry(0.008, 0.018, 4), color: c.head,
      position: [0.082, 0.112, 0.019], rotation: [0.35, 0, -0.1] },
    { geometry: new THREE.ConeGeometry(0.008, 0.018, 4), color: c.head,
      position: [0.082, 0.112, -0.019], rotation: [-0.35, 0, -0.1] },
    // bristle mane along the crest
    { geometry: new THREE.ConeGeometry(0.011, 0.032, 4), color: "#1a140e",
      position: [0.05, 0.126, 0], rotation: [0, 0, -0.28] },
    { geometry: new THREE.ConeGeometry(0.011, 0.032, 4), color: "#1a140e",
      position: [0.013, 0.134, 0], rotation: [0, 0, -0.08] },
    { geometry: new THREE.ConeGeometry(0.011, 0.032, 4), color: "#1a140e",
      position: [-0.023, 0.124, 0], rotation: [0, 0, 0.2] },
    // thin tasselled tail
    { geometry: new THREE.CylinderGeometry(0.0025, 0.0025, 0.034, 3), color: c.leg,
      position: [-0.09, 0.078, 0], rotation: [0, 0, 0.3] },
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: "#1a140e",
      position: [-0.086, 0.058, 0] },
  );
  return parts;
}

export const boarV2 = defineModel({
  id: "boar-v2",
  scale: 2.36, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Boar (v2)",
  tags: ["creature","boar","low-poly","age-of-mcp","fable"],
  summary: "Low-poly boar.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.14,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => boarParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => boarParts(1) },
  ],
  animations: [],
});
